import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import moment from 'moment'
import { FormattedMessage, useIntl, type IntlShape } from 'react-intl'
import Heading from 'LuxKit/Typography/Heading'
import Group from 'components/utils/Group'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { fetchOrders } from 'actions/OrderActions'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { nonNullable } from 'lib/array/arrayUtils'
import { areOrdersBeingFetched, haveOrdersBeenFetched } from 'lib/order/orderUtils'
import noop from 'lib/function/noop'
import { capitalise } from 'lib/string/stringUtils'
import { getFirstName, selectLoggedIn } from 'selectors/accountSelectors'
import type { OrderLinkOptions } from 'tripPlanner/assistant/runtime/chatStream'
import { collectSelectedBookings, getAddBookingsLabel } from 'tripPlanner/components/Chat/addBookingsMessage'
import ChatFileDropArea from 'tripPlanner/components/Chat/ChatFileDropArea'
import { deriveFlightDisplayData } from 'tripPlanner/components/Chat/flightDisplayUtils'
import StaffChatAccessForm from 'tripPlanner/components/Chat/StaffChatAccessForm'
import { useTripDateFormatters } from 'tripPlanner/components/Chat/useTripDateFormatters'
import { useChatFiles } from 'tripPlanner/pages/chat/ChatTripPlannerPage/contexts/ChatFilesContext'
import { useAddBookingsSubmit, type AddBookingsSend } from 'tripPlanner/pages/chat/ChatTripPlannerPage/hooks/useAddBookingsSubmit'
import { useRecentChatItems } from 'tripPlanner/pages/chat/useRecentChatItems'
import { trackEntryPoint, type EntryPointType } from 'tripPlanner/utils/chatAnalytics'
import ChatLandingChatsLoading from './ChatLandingChatsLoading'
import ChatLandingComposer from './ChatLandingComposer'
import ChatLandingRecentChats from './ChatLandingRecentChats'
import ChatLandingTryAsking from './ChatLandingTryAsking'
import ChatLandingUpcomingTrips from './ChatLandingUpcomingTrips'
import ChatLandingUpcomingTripsLoading from './ChatLandingUpcomingTripsLoading'
import type { OrderTileData } from './bookingSelectionUtils'
import { buildPlanAroundTripMessage } from './planAroundTripMessage'
import { getChatLandingUpcomingTrips } from './upcomingTrips'

const LandingContainer = styled.div`
  flex: 1;
  min-block-size: 0;
  inline-size: 100%;
  overflow-y: auto;
`

const COLUMN_GUTTER = rem(16)

const LandingColumn = styled.div`
  inline-size: 100%;
  max-inline-size: calc(${rem(720)} + 2 * ${COLUMN_GUTTER});
  margin-inline: auto;
  padding-inline: ${COLUMN_GUTTER};
  padding-block: ${rem(28)} ${rem(40)};

  ${mediaQueryUp.tablet} {
    /* Figma's 200px offset on tall screens, shrinking on short laptops so the composer stays above the fold. */
    padding-block-start: clamp(${rem(40)}, 22vh, ${rem(200)});
  }
`

function getAttachedBookingMessage(intl: IntlShape, userMessage: string): string {
  if (!userMessage) {
    return getAddBookingsLabel(intl, 1)
  }

  return intl.formatMessage(
    {
      id: 'sIbU1GoJUuBp23inTFa9',
      defaultMessage: 'Add this booking to my trip: {userMessage}',
      description: 'Visible chat message sent from the trip planner landing composer when a booking chip from an order link is attached, followed by what the traveller typed',
    },
    { userMessage },
  )
}

function renderGreetingName(chunks: ReactNode) {
  return <i key="greeting-name">{chunks}</i>
}

interface Props {
  onSubmit: (message: string) => Promise<void>
  onSubmitDirect: (message: string, options?: OrderLinkOptions) => Promise<void>
  onAddBookings: AddBookingsSend
  onStaffChatOpen?: (chatId: number) => void
  disabled: boolean
  prefilledOrderId: string | undefined
  selectedTags: Array<string>
  onRemoveTag: (tag: string) => void
}

function ChatLandingPage({
  onSubmit,
  onSubmitDirect,
  onAddBookings,
  onStaffChatOpen,
  disabled,
  prefilledOrderId,
  selectedTags,
  onRemoveTag,
}: Props) {
  const intl = useIntl()
  const dispatch = useAppDispatch()
  const isLoggedIn = useAppSelector(selectLoggedIn)
  const firstName = useAppSelector(getFirstName)

  const { items: recentChatItems, isLoading: isChatsLoading } = useRecentChatItems({ isLoggedIn })
  const upcomingOrdersFetched = useAppSelector(state => haveOrdersBeenFetched(state, 'upcoming'))
  const upcomingOrdersFetching = useAppSelector(state => areOrdersBeingFetched(state, 'upcoming'))
  useEffect(() => {
    if (isLoggedIn && !upcomingOrdersFetched) {
      dispatch(fetchOrders({ status: 'upcoming' }))
    }
  }, [dispatch, isLoggedIn, upcomingOrdersFetched])
  const [now] = useState(() => Date.now())
  const today = useMemo(() => moment(now).format(ISO_DATE_FORMAT), [now])
  const upcomingTrips = useAppSelector(state => getChatLandingUpcomingTrips(state, today))
  const flightDetailsByItemId = useAppSelector(state => state.orders.flightDetails)
  const formatters = useTripDateFormatters()

  const ordersById = useAppSelector(state => state.orders.orders)

  // The landing never has a chat: the send creates one.
  const { submittingOrderIds, submitBookings } = useAddBookingsSubmit({ chatId: undefined, isLoggedIn, onAddBookings })
  const isComposerDisabled = disabled || submittingOrderIds.length > 0
  const { files, setFiles } = useChatFiles()

  const entryPointFiredRef = useRef(false)
  const fireEntryPoint = useCallback((type: EntryPointType) => {
    if (!entryPointFiredRef.current) {
      entryPointFiredRef.current = true
      trackEntryPoint(type)
    }
  }, [])

  const prefilledTrip = useMemo(() => {
    return upcomingTrips.find(trip => trip.orderId === prefilledOrderId)
  }, [prefilledOrderId, upcomingTrips])

  const submitTripBookings = useCallback((trip: OrderTileData, visibleMessage: string) => {
    const orders = nonNullable([ordersById[trip.orderId]])
    return submitBookings({
      selectedBookings: collectSelectedBookings(orders, new Set(trip.bookingItemIds)),
      originalOrders: orders,
      visibleMessage,
    })
  }, [ordersById, submitBookings])

  const handleComposerSubmit = useCallback(async(message: string) => {
    fireEntryPoint('typed_prompt')
    if (!prefilledTrip) {
      onSubmit(message).catch(noop)
      return
    }

    const visibleMessage = getAttachedBookingMessage(intl, message.trim())
    if (await submitTripBookings(prefilledTrip, visibleMessage) === 'unavailable') {
      onSubmit(message).catch(noop)
    }
  }, [fireEntryPoint, intl, onSubmit, prefilledTrip, submitTripBookings])

  const handlePlanAroundTrip = useCallback(async(trip: OrderTileData) => {
    fireEntryPoint('booking_card')
    const flightDetails = trip.primaryFlightItemId ? flightDetailsByItemId[trip.primaryFlightItemId]?.flight : undefined
    const flight = flightDetails && deriveFlightDisplayData(flightDetails)
    const visibleMessage = buildPlanAroundTripMessage(intl, trip, formatters, flight)
    if (await submitTripBookings(trip, visibleMessage) === 'unavailable') {
      onSubmitDirect(visibleMessage, { orderIds: [trip.orderId] }).catch(noop)
    }
  }, [fireEntryPoint, flightDetailsByItemId, formatters, intl, onSubmitDirect, submitTripBookings])

  const handleTryAskingPrompt = useCallback((prompt: string) => {
    fireEntryPoint('suggestion_chip')
    onSubmitDirect(prompt).catch(noop)
  }, [fireEntryPoint, onSubmitDirect])

  const handleRecentChatClick = useCallback(() => {
    fireEntryPoint('recent_trip_card')
  }, [fireEntryPoint])

  const isUpcomingTripsLoading = isLoggedIn && !upcomingOrdersFetched && upcomingOrdersFetching
  const showUpcomingTrips = isLoggedIn && upcomingOrdersFetched && upcomingTrips.length > 0
  const showRecentChats = !isChatsLoading && isLoggedIn && recentChatItems.length > 0
  const showTryAsking = !isChatsLoading && !showRecentChats
  const greetingName = firstName ? capitalise(firstName) : ''

  return (
    <ChatFileDropArea files={files} onFilesChange={setFiles} disabled={isComposerDisabled}>
      <LandingContainer>
        <LandingColumn>
          <Group direction="vertical" gap={28} tabletGap={40} fullWidth>
            {/* The extra desktop gap keeps the trip details nudge above the composer from covering the heading. */}
            <Group direction="vertical" gap={24} desktopGap={40} fullWidth>
              <Heading variant="heading1" as="h1">
                {greetingName && (
                  <FormattedMessage
                    id="VxDiNtREk2SkhtHDtcVD"
                    defaultMessage="<i>Hi {firstName},</i> start planning your next trip"
                    description="Greeting above the trip planner chat composer for a logged-in traveller, with their first name in italics"
                    values={{ firstName: greetingName, i: renderGreetingName }}
                  />
                )}
                {!greetingName && (
                  <FormattedMessage
                    id="g6gyLy_bjVCOgIpEPmla"
                    defaultMessage="<i>Hi there,</i> start planning your next trip"
                    description="Greeting above the trip planner chat composer when the traveller's name is unknown"
                    values={{ i: renderGreetingName }}
                  />
                )}
              </Heading>
              <ChatLandingComposer
                onSubmit={handleComposerSubmit}
                disabled={isComposerDisabled}
                selectedTags={selectedTags}
                onRemoveTag={onRemoveTag}
              />
            </Group>
            {isUpcomingTripsLoading && <ChatLandingUpcomingTripsLoading />}
            {showUpcomingTrips && (
              <ChatLandingUpcomingTrips
                trips={upcomingTrips}
                disabled={isComposerDisabled}
                submittingOrderIds={submittingOrderIds}
                onPlanClick={handlePlanAroundTrip}
              />
            )}
            {isChatsLoading && <ChatLandingChatsLoading />}
            {showRecentChats && (
              <ChatLandingRecentChats items={recentChatItems} now={now} onChatClick={handleRecentChatClick} />
            )}
            {showTryAsking && (
              <ChatLandingTryAsking disabled={isComposerDisabled} onSelectPrompt={handleTryAskingPrompt} />
            )}
            {onStaffChatOpen && <StaffChatAccessForm onOpenChat={onStaffChatOpen} />}
          </Group>
        </LandingColumn>
      </LandingContainer>
    </ChatFileDropArea>
  )
}

export default ChatLandingPage
